/**
 * V4 Chat Simulation Hook
 * Manages chat-based simulation interactions with separate conversation memory per agent
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { getAuthHeaders } from '../../utils/api';

// Types for chat-based simulation
export interface ChatMessage {
  message_id: string;
  sender: string;
  content: string;
  timestamp: string;
  message_type: 'text' | 'system' | 'typing' | 'action';
  metadata?: Record<string, any>;
}

export interface Agent {
  agent_id: string;
  name: string;
  role: string;
  is_typing: boolean;
  last_activity: string | null;
  message_count: number;
}

export interface Task {
  task_id: string;
  title: string;
  status: 'pending' | 'in_progress' | 'completed' | 'blocked';
  priority: 'low' | 'medium' | 'high';
  category: string;
}

export interface SimulationMetrics {
  [key: string]: number;
}

export interface EnvironmentStatus {
  description: string;
  rules: string[];
  current_state: Record<string, any>;
}

export interface ChatSimulationState {
  session_id: string;
  status: string;
  current_step: number;
  agents: Agent[];
  tasks: Task[];
  metrics: SimulationMetrics;
  environment: EnvironmentStatus;
  selected_agent_id: string | null;
  conversations: Record<string, ChatMessage[]>;
  typing_agents: Set<string>;
  metadata?: {
    simulation_type?: string;
    [key: string]: any;
  };
}

interface UseV4ChatOptions {
  preloadedSessionId?: string | null;
}

const useV4Chat = (options: UseV4ChatOptions = {}) => {
  const { preloadedSessionId } = options;

  // Core state
  const [simulationState, setSimulationState] = useState<ChatSimulationState | null>(null);
  const [taskList, setTaskList] = useState<any | null>(null);
  const [semanticProfile, setSemanticProfile] = useState<any | null>(null);
  const [finalEvaluation, setFinalEvaluation] = useState<any | null>(null);
  const [isGeneratingEvaluation, setIsGeneratingEvaluation] = useState(false);
  const [timeTracking, setTimeTracking] = useState<any | null>(null);
  const [showTimeExpiredModal, setShowTimeExpiredModal] = useState(false);
  const [isNavigatingAway, setIsNavigatingAway] = useState(false);
  const [eventCodeMap, setEventCodeMap] = useState<Record<string, import('../types').EventCodeState>>({});

  // Frontend-controlled typing state for in-chat typing indicator
  // This is set when user sends a message and cleared when response arrives
  const [activelyRespondingAgent, setActivelyRespondingAgent] = useState<string | null>(null);

  // Unread message tracking per agent
  const [unreadAgents, setUnreadAgents] = useState<Set<string>>(new Set());
  
  // New message notification callback - can be set by the component to show toast notifications
  const newMessageCallbackRef = useRef<((agentId: string, agentName: string, messagePreview: string) => void) | null>(null);

  // Debug wrapper for setTaskList with stability check to prevent flickering
  const debugSetTaskList = (newValue: any) => {
    // Stability check - only update if structure is meaningfully different
    if (taskList && newValue) {
      const hasStructuralDifference = (
        Boolean(taskList.primary_goal) !== Boolean(newValue.primary_goal) ||
        (taskList.sub_objectives?.length || 0) !== (newValue.sub_objectives?.length || 0) ||
        (taskList.objectives?.length || 0) !== (newValue.objectives?.length || 0) ||
        taskList.total_objectives !== newValue.total_objectives
      );

      if (!hasStructuralDifference) {

        // Check for meaningful content changes (health, progress, contributions)
        const hasContentChanges = taskList.objectives?.some((oldObj: any, index: number) => {
          const newObj = newValue.objectives?.[index];
          return newObj && (
            Math.abs(oldObj.health_score - newObj.health_score) > 0.01 ||
            Math.abs(oldObj.current_progress - newObj.current_progress) > 0.01 ||
            oldObj.last_contribution !== newObj.last_contribution
          );
        });

        if (!hasContentChanges) {
          return; // Don't trigger re-render
        }

      }
    }

    setTaskList(newValue);
  };
  const [objectiveUpdates, setObjectiveUpdates] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load preloaded session if provided
  useEffect(() => {
    if (preloadedSessionId && !simulationState) {
      loadChatData(preloadedSessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preloadedSessionId]);

  // Chat-specific state
  const [messageInput, setMessageInput] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);

  // Refs for auto-scroll and polling
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const pollingIntervalRef = useRef<number | null>(null);
  const evaluationPollingIntervalRef = useRef<number | null>(null);
  const previousTypingAgentsRef = useRef<Set<string>>(new Set());
  const recentlyAddedMessageIdsRef = useRef<Set<string>>(new Set());

  // Track last message ID per agent for efficient polling
  const lastMessageIdsRef = useRef<Record<string, string>>({});
  const conversationStateCorruptedRef = useRef<boolean>(false);
  const isSendingRef = useRef<boolean>(false);
  
  // Track current selected agent ID for notification logic (avoids stale closures)
  const selectedAgentIdRef = useRef<string | null>(null);
  
  // Track current agents list for notification logic (avoids stale closures)
  const agentsRef = useRef<Agent[]>([]);

  // API base URL
  const apiBaseUrl = import.meta.env.VITE_V4_API_BASE ||
    (import.meta.env.VITE_API_BASE ? `${import.meta.env.VITE_API_BASE}/api/v4` : null) ||
    'http://localhost:5000/api/v4';

  // Utility function for API calls
  const handleApiCall = useCallback(async (endpoint: string, options: RequestInit = {}) => {
    try {
      const url = `${apiBaseUrl}${endpoint}`;

      // Get auth headers for authenticated requests
      const authHeaders = await getAuthHeaders();

      // Don't set Content-Type for FormData uploads - let browser handle it
      const isFormData = options.body instanceof FormData;
      const defaultHeaders = isFormData ? {} : {
        'Content-Type': 'application/json',
      };

      const response = await fetch(url, {
        ...options,
        headers: {
          ...defaultHeaders,
          ...authHeaders,
          ...options.headers,
        },
      });

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch {
          errorData = { error: `HTTP ${response.status}: ${response.statusText}` };
        }
        console.error('useV4Chat: API error response', errorData);

        // Include status code in error message for better error handling
        const errorMessage = errorData.message || errorData.error || `HTTP ${response.status}: ${response.statusText}`;
        throw new Error(`${response.status}: ${errorMessage}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('useV4Chat: API call failed', { endpoint, error });
      throw error;
    }
  }, [apiBaseUrl]);

  // Initialize simulation from blueprint
  const initializeSimulation = useCallback(async (blueprintFile: File): Promise<void> => {
    setIsLoading(true);
    setError(null);

    const formData = new FormData();
    formData.append('blueprint', blueprintFile);

    try {
      const result = await handleApiCall('/initialize', {
        method: 'POST',
        body: formData,
        // Headers will be set automatically by handleApiCall for FormData
      });

      const sessionId = result.data.simulation_state.session_id;

      // Fetch initial data for chat interface
      await loadChatData(sessionId);
    } catch (error) {
      console.error('ERROR: Simulation initialization failed:', error);
      setError(error instanceof Error ? error.message : 'Unknown initialization error');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [handleApiCall]);

  // Rebuild conversation state from full history (for state recovery)
  const rebuildConversationState = useCallback((conversationData: Record<string, any>) => {
    const conversations: Record<string, ChatMessage[]> = {};
    const lastMessageIds: Record<string, string> = {};

    Object.keys(conversationData).forEach(agentId => {
      const agentConv = conversationData[agentId];
      const messages = agentConv.messages || [];
      conversations[agentId] = messages;

      // Track last message ID for this agent
      if (messages.length > 0) {
        const lastMessage = messages[messages.length - 1];
        lastMessageIds[agentId] = lastMessage.message_id;
      }
    });

    lastMessageIdsRef.current = lastMessageIds;
    conversationStateCorruptedRef.current = false;

    return conversations;
  }, []);

  // Load all chat-related data
  const loadChatData = useCallback(async (sessionId: string) => {
    try {
      // Load agents, tasks, and full conversation history in parallel
      const [agentsResponse, tasksResponse, conversationsResponse] = await Promise.all([
        handleApiCall(`/chat/agents?session_id=${sessionId}`),
        handleApiCall(`/chat/tasks?session_id=${sessionId}`),
        handleApiCall(`/chat/conversations?session_id=${sessionId}`)
      ]);

      // Parse conversations and rebuild state
      const conversationData = conversationsResponse.data?.data?.conversations || conversationsResponse.data?.conversations || {};
      const conversations = rebuildConversationState(conversationData);

      // Update agents ref for notification logic
      agentsRef.current = agentsResponse.data.agents;
      
      // Set up simulation state
      setSimulationState({
        session_id: sessionId,
        status: 'running',
        current_step: 0,
        agents: agentsResponse.data.agents,
        tasks: tasksResponse.data.tasks,
        metrics: {}, // Will be loaded separately
        environment: {
          description: 'Professional workplace environment',
          rules: ['Maintain professional communication', 'Respect confidentiality'],
          current_state: {}
        },
        selected_agent_id: null,
        conversations,
        typing_agents: new Set(),
        metadata: agentsResponse.data.metadata || {}
      });

      // Load initial metrics and task list with a small delay to ensure session is ready
      setTimeout(async () => {
        try {
          const metricsResponse = await handleApiCall(`/status?session_id=${sessionId}`);
          if (metricsResponse && metricsResponse.status === 'success' && metricsResponse.data && metricsResponse.data.performance_metrics) {
            setSimulationState(prev => {
              const newState = prev ? {
                ...prev,
                metrics: metricsResponse.data.performance_metrics
              } : null;
              return newState;
            });
          }
        } catch (metricsError) {
          console.error('ERROR: Failed to load metrics:', metricsError);
        }
      }, 2000); // Wait 2 seconds for session to be fully initialized

      // Load task list - add error handling 
      try {
        await loadTaskList(sessionId);
      } catch (taskListError) {
        console.error('❌ Failed to load task list during initialization:', taskListError);
      }

      // Load semantic profile
      try {
        await loadSemanticProfile(sessionId);
      } catch (profileError) {
        console.error('❌ Failed to load semantic profile during initialization:', profileError);
      }

      // Start polling for evaluation results
      startEvaluationPolling(sessionId);

      // Start polling for updates
      startPolling(sessionId);

    } catch (error) {
      console.error('Failed to load chat data:', error);
      setError(error instanceof Error ? error.message : 'Failed to load chat data');
    }
  }, [handleApiCall]);

  // Load task list and objectives
  const loadTaskList = useCallback(async (sessionId: string) => {
    try {
      const taskResponse = await handleApiCall(`/chat/tasks?session_id=${sessionId}`);

      if (taskResponse && taskResponse.status === 'success' && taskResponse.data) {
        debugSetTaskList(taskResponse.data);
      }
    } catch (taskError) {
      console.error('ERROR: Failed to load task list:', taskError);
    }
  }, [handleApiCall]);

  // Load semantic profile and simulation health
  const loadSemanticProfile = useCallback(async (sessionId: string) => {
    try {
      const profileResponse = await handleApiCall(`/semantic/profile?session_id=${sessionId}`);

      if (profileResponse && profileResponse.status === 'success' && profileResponse.data) {
        setSemanticProfile(profileResponse.data);
      }
    } catch (profileError) {
      console.error('ERROR: Failed to load semantic profile:', profileError);
    }
  }, [handleApiCall]);

  // Poll for background evaluation results
  const pollEvaluationResults = useCallback(async (sessionId: string) => {
    try {
      const evaluationResponse = await handleApiCall(`/evaluation/poll?session_id=${sessionId}`);

      if (evaluationResponse && evaluationResponse.status === 'success' && evaluationResponse.data) {
        const { has_updates, evaluation_updates } = evaluationResponse.data;

        if (has_updates && evaluation_updates.evaluations_run) {

          // Update objective updates state
          setObjectiveUpdates({
            evaluations_run: true,
            objective_changes: evaluation_updates.objective_changes,
            next_evaluation_in: 0, // Reset since we just got results
            total_tasks_completed: evaluation_updates.total_tasks_completed
          });

          // Update task list if provided (and not null/undefined)
          if (evaluation_updates.updated_task_list && typeof evaluation_updates.updated_task_list === 'object') {
            debugSetTaskList(evaluation_updates.updated_task_list);
          } else {
          }
        }
      }
    } catch (error) {
      // Silently ignore evaluation polling errors
    }
  }, [handleApiCall]);

  // Start polling for evaluation results
  const startEvaluationPolling = useCallback((sessionId: string) => {
    if (evaluationPollingIntervalRef.current) {
      clearInterval(evaluationPollingIntervalRef.current);
    }

    evaluationPollingIntervalRef.current = setInterval(async () => {
      await pollEvaluationResults(sessionId);
    }, 3000) as unknown as number; // Poll every 3 seconds for evaluation results
  }, [pollEvaluationResults]);

  // Poll latest messages from all agents
  const pollLatestMessages = useCallback(async (sessionId: string) => {
    // Skip polling if we're currently sending a message to avoid race conditions
    if (isSendingRef.current) {
      return;
    }

    try {
      const latestResponse = await handleApiCall(`/chat/conversations/latest?session_id=${sessionId}`);
      const responseData = latestResponse.data?.data || latestResponse.data;

      if (!responseData || !responseData.latest_messages) {
        // If response structure is unexpected, mark state as potentially corrupted
        if (!conversationStateCorruptedRef.current) {
          console.warn('Unexpected latest messages response structure, may need state recovery');
        }
        return;
      }

      const latestMessages = responseData.latest_messages;
      const stateNeedsRebuildRef = { value: false };
      
      // BEFORE state update: Detect new messages by comparing with our tracking refs
      // This avoids issues with React StrictMode calling state updaters twice
      const newMessagesForNotification: Array<{ agentId: string; message: ChatMessage }> = [];
      
      Object.keys(latestMessages).forEach(agentId => {
        const agentLatest = latestMessages[agentId];
        const latestMessage = agentLatest.latest_message;
        
        if (latestMessage && latestMessage.sender !== 'user') {
          const lastKnownId = lastMessageIdsRef.current[agentId];
          // If we don't know this message yet, it's new
          if (lastKnownId !== latestMessage.message_id) {
            newMessagesForNotification.push({ agentId, message: latestMessage });
          }
        }
      });

      // State update (pure function, no side effects)
      setSimulationState(prev => {
        if (!prev) {
          console.warn('[pollLatestMessages] Simulation state is null, cannot update');
          return prev;
        }

        // Initialize lastMessageIdsRef from existing conversations if not set
        Object.keys(prev.conversations || {}).forEach(agentId => {
          const messages = prev.conversations[agentId];
          if (messages && messages.length > 0 && !lastMessageIdsRef.current[agentId]) {
            const lastMsg = messages[messages.length - 1];
            lastMessageIdsRef.current[agentId] = lastMsg.message_id;
          }
        });

        const updatedConversations = { ...(prev.conversations || {}) };
        const updatedTypingAgents = new Set(prev.typing_agents || []);
        let hasUpdates = false;

        // Process latest messages from each agent
        Object.keys(latestMessages).forEach(agentId => {
          const agentLatest = latestMessages[agentId];
          const latestMessage = agentLatest.latest_message;

          if (!latestMessage) {
            // No messages yet for this agent
            if (!updatedConversations[agentId]) {
              updatedConversations[agentId] = [];
            }
            // Still update typing status even if no message
            if (agentLatest.is_typing !== undefined) {
              if (agentLatest.is_typing) {
                updatedTypingAgents.add(agentId);
              } else {
                updatedTypingAgents.delete(agentId);
              }
            }
            return;
          }

          const newMessageId = latestMessage.message_id;

          // Always check if message exists in actual conversation state
          const existingMessages = updatedConversations[agentId] || [];

          // Check if base message or any chunk exists
          const baseMessageExists = existingMessages.some((m: ChatMessage) => m.message_id === newMessageId);
          const chunkExists = existingMessages.some((m: ChatMessage) =>
            m.message_id.startsWith(newMessageId + '_chunk_')
          );
          const messageExists = baseMessageExists || chunkExists;

          // If message doesn't exist in state (no base or chunks), add it
          if (!messageExists) {
            if (!updatedConversations[agentId]) {
              updatedConversations[agentId] = [];
            }
            updatedConversations[agentId] = [...updatedConversations[agentId], latestMessage];
            lastMessageIdsRef.current[agentId] = newMessageId;
            hasUpdates = true;
          }

          // Update typing status
          if (agentLatest.is_typing !== undefined) {
            const wasTyping = updatedTypingAgents.has(agentId);
            if (agentLatest.is_typing) {
              updatedTypingAgents.add(agentId);
              if (!wasTyping) hasUpdates = true;
            } else {
              updatedTypingAgents.delete(agentId);
              if (wasTyping) hasUpdates = true;
            }
          }
        });

        if (stateNeedsRebuildRef.value) {
          conversationStateCorruptedRef.current = true;
        }

        if (hasUpdates) {
          return {
            ...prev,
            conversations: updatedConversations,
            typing_agents: updatedTypingAgents
          };
        }

        return prev;
      });
      
      // Process notifications AFTER state update completes
      // Use setTimeout to ensure this runs after React's state batching
      if (newMessagesForNotification.length > 0) {
        setTimeout(() => {
          const currentSelectedAgentId = selectedAgentIdRef.current;
          
          newMessagesForNotification.forEach(({ agentId, message }) => {
            // Mark as unread if user is NOT currently viewing this agent's conversation
            if (currentSelectedAgentId !== agentId) {
              setUnreadAgents(prev => new Set([...prev, agentId]));
            }
            
            // Trigger notification callback
            if (newMessageCallbackRef.current) {
              const agents = agentsRef.current;
              const agent = agents.find(a => a.agent_id === agentId);
              const agentName = agent?.name || agentId;
              const messagePreview = message.content.substring(0, 100) + (message.content.length > 100 ? '...' : '');
              newMessageCallbackRef.current(agentId, agentName, messagePreview);
            }
          });
        }, 0);
      }

      // If state is corrupted, rebuild from full history
      if (stateNeedsRebuildRef.value && conversationStateCorruptedRef.current) {
        console.log('State corrupted, rebuilding from full history...');
        try {
          const fullHistoryResponse = await handleApiCall(`/chat/conversations?session_id=${sessionId}`);
          const fullHistoryData = fullHistoryResponse.data?.data?.conversations || fullHistoryResponse.data?.conversations || {};
          const rebuiltConversations = rebuildConversationState(fullHistoryData);

          setSimulationState(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              conversations: rebuiltConversations
            };
          });
        } catch (rebuildError) {
          console.error('Failed to rebuild conversation state:', rebuildError);
        }
      }
    } catch (error) {
      console.error('Failed to poll latest messages:', error);
      // On error, mark state as potentially corrupted
      conversationStateCorruptedRef.current = true;
    }
  }, [handleApiCall, rebuildConversationState]);

  // Start polling for typing status and latest messages
  const startPolling = useCallback((sessionId: string) => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    pollingIntervalRef.current = setInterval(async () => {
      try {
        // Poll typing status
        const typingResponse = await handleApiCall(`/chat/typing?session_id=${sessionId}`);
        if (!typingResponse || !typingResponse.data || !typingResponse.data.typing_status) {
          return;
        }
        const typingStatus = typingResponse.data.typing_status;

        // Update typing status for sidebar only (backend status)
        const typingAgents = new Set<string>();
        Object.keys(typingStatus).forEach(agentId => {
          if (typingStatus[agentId]) {
            typingAgents.add(agentId);
          }
        });

        // Detect agents who just finished typing (were typing before, not typing now)
        const agentsWhoFinishedTyping: string[] = [];
        previousTypingAgentsRef.current.forEach(agentId => {
          if (!typingAgents.has(agentId)) {
            agentsWhoFinishedTyping.push(agentId);
          }
        });

        // Update the ref for next comparison
        previousTypingAgentsRef.current = typingAgents;

        // Poll latest messages (instead of reloading full conversations)
        await pollLatestMessages(sessionId);

        setSimulationState(prev => {
          if (!prev) {
            return prev;
          }

          return {
            ...prev,
            typing_agents: typingAgents
          };
        });

        // Poll metrics every few cycles (more frequently)
        if (Math.random() < 0.8) { // 80% chance each poll
          try {
            const metricsResponse = await handleApiCall(`/status?session_id=${sessionId}`);

            if (metricsResponse && metricsResponse.status === 'success' && metricsResponse.data) {
              // Update metrics
              if (metricsResponse.data.performance_metrics) {
                setSimulationState(prev => {
                  if (!prev) {
                    return prev;
                  }
                  return {
                    ...prev,
                    metrics: metricsResponse.data.performance_metrics
                  };
                });
              }

              // Update time tracking
              if (metricsResponse.data.time_tracking) {
                const timeData = metricsResponse.data.time_tracking;
                setTimeTracking(timeData);

                // Show modal if time expired and modal hasn't been shown yet
                if (timeData.is_time_expired && !showTimeExpiredModal) {
                  setShowTimeExpiredModal(true);
                }
              }
            }
          } catch (metricsError) {
            console.error('Metrics polling error:', metricsError);
          }

          // Also poll semantic profile for simulation health
          try {
            await loadSemanticProfile(sessionId);
          } catch (profileError) {
            // Silently ignore semantic profile polling errors
          }
        }

        // Task list polling is handled by evaluation polling to avoid conflicts
        // Removed duplicate task list polling from here

      } catch (error) {
        console.error('Polling error:', error);

        // Check if it's a session expiry error (404)
        if (error instanceof Error && (error.message.includes('404') || error.message.includes('Session expired'))) {
          console.error('Session expired - stopping polling and showing error');
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          setError('Session expired. Please reload the page and upload your simulation again.');
          return;
        }

        // Don't set error state for other polling failures to avoid disrupting UI
      }
    }, 2000) as unknown as number; // Poll every 2 seconds

    // Return cleanup function
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [handleApiCall, pollLatestMessages, showTimeExpiredModal, loadSemanticProfile]);

  // Send message to agent
  const sendMessage = useCallback(async (agentId: string, message: string): Promise<void> => {
    if (!simulationState || !message.trim()) return;

    setIsSendingMessage(true);
    isSendingRef.current = true; // Pause polling during send
    setMessageInput('');

    // Set agent as actively responding (for in-chat typing indicator)
    setActivelyRespondingAgent(agentId);

    try {
      // Optimistically add user message to UI
      const userMessageId = `msg_${Date.now()}_user`;
      const userMessage: ChatMessage = {
        message_id: userMessageId,
        sender: 'user',
        content: message,
        timestamp: new Date().toISOString(),
        message_type: 'text'
      };

      setSimulationState(prev => {
        if (!prev) {
          return prev;
        }

        const conversationHistory = prev.conversations[agentId] || [];

        // Check if message already exists (avoid duplicate optimistic adds)
        const messageExists = conversationHistory.some(
          (m: ChatMessage) => m.sender === 'user' && m.content === message
        );

        if (messageExists) {
          console.log('User message already exists, skipping duplicate');
          return prev;
        }

        const updatedConversations = {
          ...prev.conversations,
          [agentId]: [...conversationHistory, userMessage]
        };

        return {
          ...prev,
          conversations: updatedConversations
        };
      });

      // Send message to API
      const response = await handleApiCall('/process_turn', {
        method: 'POST',
        body: JSON.stringify({
          session_id: simulationState.session_id,
          agent_id: agentId,
          message: message
        })
      });

      // Handle skip_response (trivial tolerance exceeded)
      // API returns: {status: "success", message: "...", data: {skip_response: true, ...}}
      const skipResponseData = response.data?.data || response.data;
      if (skipResponseData && (response.data?.status === 'success' || response.data?.success) && skipResponseData.skip_response) {
        setActivelyRespondingAgent(null);
        setIsSendingMessage(false);

        // Log for debugging but don't show any UI message
        console.warn(`Agent response skipped: ${response.data.reason}`);
        console.warn(`Consecutive trivial inputs: ${response.data.consecutive_trivial_count}`);
        console.info('User messages will be visible, but no agent response will be shown.');

        // Don't add any message bubble - just skip the agent response entirely
        return;
      }

      // Update conversation with agent response (V4 API structure)
      // API returns: {status: "success", message: "...", data: {response: "...", agent_message: {...}, ...}}
      const responseData = response.data?.data || response.data;
      if (responseData && (response.data?.status === 'success' || response.data?.success) && responseData.response) {
        // Get backend message_id if available (from agent_message in response)
        const backendAgentMessage = responseData.agent_message;
        const backendMessageId = backendAgentMessage?.message_id;
        const backendTimestamp = backendAgentMessage?.timestamp;

        console.log('Processing agent response:', {
          hasBackendMessageId: !!backendMessageId,
          backendMessageId,
          responseLength: responseData.response?.length
        });

        // Backend provides chunks - display them progressively with separate bubbles
        const chunks = responseData.chunks || [responseData.response];
        const typingDelay = responseData.typing_delay || 0.5;
        const baseMessageId = backendMessageId || `msg_${Date.now()}_agent`;

        for (let i = 0; i < chunks.length; i++) {
          const chunk = chunks[i];

          // Show typing indicator before each chunk (except first)
          if (i > 0) {
            setActivelyRespondingAgent(agentId);
            await new Promise(resolve => setTimeout(resolve, typingDelay * 1000));
          }

          // Clear typing and add chunk as separate message
          setActivelyRespondingAgent(null);

          setSimulationState(prev => {
            if (!prev) return prev;

            const conversationHistory = prev.conversations[agentId] || [];

            // Create unique message ID for each chunk
            const chunkMessageId = i === 0 ? baseMessageId : `${baseMessageId}_chunk_${i}`;

            // Check if this chunk already exists
            const messageExists = conversationHistory.some(
              (m: ChatMessage) => m.message_id === chunkMessageId
            );

            if (messageExists) {
              return prev;
            }

            // Create new message bubble for this chunk
            const agentMessage: ChatMessage = {
              message_id: chunkMessageId,
              sender: agentId,
              content: chunk,
              timestamp: backendTimestamp || new Date().toISOString(),
              message_type: 'text'
            };

            // Update last message ID tracking on first chunk
            if (i === 0 && backendMessageId) {
              lastMessageIdsRef.current[agentId] = backendMessageId;
            }

            const updatedConversation = [...conversationHistory, agentMessage];

            return {
              ...prev,
              conversations: {
                ...prev.conversations,
                [agentId]: updatedConversation
              }
            };
          });

          // Small delay for UI to update
          await new Promise(resolve => setTimeout(resolve, 50));
        }

        // Auto-scroll to bottom after state update (only if user is viewing this agent's chat)
        if (simulationState.selected_agent_id === agentId) {
          setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
          }, 100);
        }
        
        // Trigger toast notification for the agent response
        if (newMessageCallbackRef.current && chunks.length > 0) {
          const agent = simulationState.agents.find(a => a.agent_id === agentId);
          const agentName = agent?.name || agentId;
          const fullResponse = chunks.join(' ');
          const messagePreview = fullResponse.substring(0, 100) + (fullResponse.length > 100 ? '...' : '');
          newMessageCallbackRef.current(agentId, agentName, messagePreview);
        }
      } else {
        // Response data is invalid or missing
        console.error('API response missing data or not successful:', response);
        setActivelyRespondingAgent(null);
      }

      // Update objective updates if present
      if (response.data && response.data.objective_updates) {
        setObjectiveUpdates(response.data.objective_updates);

        // Refresh task list if objectives were evaluated
        if (response.data.objective_updates.evaluations_run && simulationState.session_id) {
          await loadTaskList(simulationState.session_id);
        }
      }

      // Update task list from turn results if present
      if (response.data && response.data.task_list) {
        debugSetTaskList(response.data.task_list);
      }

    } catch (error) {
      console.error('Failed to send message:', error);
      setError(error instanceof Error ? error.message : 'Failed to send message');

      // Clear typing status on error
      setActivelyRespondingAgent(null);

      // Remove optimistic message on error
      setSimulationState(prev => {
        if (!prev) {
          return prev;
        }

        const updatedConversations = {
          ...prev.conversations,
          [agentId]: prev.conversations[agentId]?.slice(0, -1) || []
        };

        return {
          ...prev,
          conversations: updatedConversations
        };
      });
    } finally {
      setIsSendingMessage(false);
      isSendingRef.current = false; // Resume polling
    }
  }, [simulationState, handleApiCall, loadTaskList]);

  // Select agent for chat
  const selectAgent = useCallback((agentId: string | null) => {
    // Update ref for use in polling callbacks (avoids stale closures)
    selectedAgentIdRef.current = agentId;
    
    setSimulationState(prev => {
      if (!prev) {
        return prev;
      }
      return {
        ...prev,
        selected_agent_id: agentId
      };
    });

    // Clear unread status for this agent when they're selected
    if (agentId) {
      setUnreadAgents(prev => {
        const next = new Set(prev);
        next.delete(agentId);
        return next;
      });
    }

    // Auto-scroll to bottom when switching agents
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }, []);

  // Update task status
  const updateTask = useCallback((taskId: string, status: Task['status']) => {
    setSimulationState(prev => {
      if (!prev) {
        return prev;
      }

      const updatedTasks = prev.tasks.map(task =>
        task.task_id === taskId ? { ...task, status } : task
      );

      return {
        ...prev,
        tasks: updatedTasks
      };
    });
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Set new message callback for toast notifications
  const setNewMessageCallback = useCallback((callback: ((agentId: string, agentName: string, messagePreview: string) => void) | null) => {
    newMessageCallbackRef.current = callback;
  }, []);

  // Check if an agent has unread messages
  const hasUnreadMessages = useCallback((agentId: string) => {
    return unreadAgents.has(agentId);
  }, [unreadAgents]);

  // Clear all unread messages
  const clearAllUnread = useCallback(() => {
    setUnreadAgents(new Set());
  }, []);

  // Generate final evaluation
  const generateFinalEvaluation = useCallback(async (candidateName?: string): Promise<void> => {
    if (!simulationState) {
      console.warn('generateFinalEvaluation: No simulation state');
      return;
    }
    
    if (isGeneratingEvaluation) {
      console.warn('generateFinalEvaluation: Already generating evaluation');
      return;
    }

    setIsGeneratingEvaluation(true);
    setError(null);

    try {
      console.log('Generating final evaluation for session:', simulationState.session_id);
      const result = await handleApiCall('/final_evaluation', {
        method: 'POST',
        body: JSON.stringify({
          session_id: simulationState.session_id,
          candidate_name: candidateName
        })
      });

      if (result.data && result.data.evaluation) {
        setFinalEvaluation(result.data.evaluation);
        console.log('Final evaluation generated successfully');
      } else {
        throw new Error('Invalid evaluation response');
      }
    } catch (error) {
      console.error('Failed to generate final evaluation:', error);
      setError(error instanceof Error ? error.message : 'Failed to generate final evaluation');
      throw error;
    } finally {
      setIsGeneratingEvaluation(false);
    }
  }, [simulationState, isGeneratingEvaluation, handleApiCall]);

  // Fetch code for a dynamic event
  const fetchEventCode = useCallback(async (eventId: string): Promise<void> => {
    if (!simulationState) return;

    setEventCodeMap(prev => ({
      ...prev,
      [eventId]: { isLoading: true, isPushing: false }
    }));

    try {
      const result = await handleApiCall(`/sessions/${simulationState.session_id}/events/${eventId}/generate_code`, {
        method: 'POST'
      });

      if (result.data) {
        setEventCodeMap(prev => ({
          ...prev,
          [eventId]: {
            isLoading: false,
            code: result.data,
            isPushing: false
          }
        }));
        console.log(`Code generated for event ${eventId}`);
      } else {
        throw new Error('Invalid code generation response');
      }
    } catch (error) {
      console.error(`Failed to fetch code for event ${eventId}:`, error);
      setEventCodeMap(prev => ({
        ...prev,
        [eventId]: {
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to generate code',
          isPushing: false
        }
      }));
    }
  }, [simulationState, handleApiCall]);

  // Push edited code for review
  const pushEventCode = useCallback(async (eventId: string, code: string, filename: string, language: string): Promise<void> => {
    if (!simulationState) return;

    setEventCodeMap(prev => ({
      ...prev,
      [eventId]: { ...prev[eventId], isPushing: true }
    }));

    try {
      const result = await handleApiCall(`/sessions/${simulationState.session_id}/events/${eventId}/push_code`, {
        method: 'POST',
        body: JSON.stringify({
          code,
          filename,
          language
        })
      });

      if (result.data) {
        setEventCodeMap(prev => ({
          ...prev,
          [eventId]: { ...prev[eventId], isPushing: false }
        }));
        console.log(`Code pushed for event ${eventId}`);

        await pollLatestMessages(simulationState.session_id);
      } else {
        throw new Error('Invalid push response');
      }
    } catch (error) {
      console.error(`Failed to push code for event ${eventId}:`, error);
      setEventCodeMap(prev => ({
        ...prev,
        [eventId]: {
          ...prev[eventId],
          isPushing: false,
          error: error instanceof Error ? error.message : 'Failed to push code'
        }
      }));
      throw error;
    }
  }, [simulationState, handleApiCall, pollLatestMessages]);

  // Report voice mode usage to the backend
  const reportVoiceModeUsage = useCallback(async (voiceModeUsed: boolean, voiceModeSeconds: number): Promise<void> => {
    if (!simulationState) return;

    const sessionId = simulationState.session_id;
    
    try {
      await handleApiCall(`/sessions/${sessionId}/voice-usage`, {
        method: 'POST',
        body: JSON.stringify({
          voice_mode_used: voiceModeUsed,
          voice_mode_seconds: voiceModeSeconds
        })
      });
      console.log(`Voice mode usage reported for session ${sessionId}: used=${voiceModeUsed}, seconds=${voiceModeSeconds}`);
    } catch (error) {
      console.error(`Failed to report voice mode usage for session ${sessionId}:`, error);
    }
  }, [simulationState, handleApiCall]);

  // Cleanup session and stop all background tasks
  const cleanupSession = useCallback(async (voiceModeUsed?: boolean, voiceModeSeconds?: number): Promise<void> => {
    if (!simulationState) return;

    const sessionId = simulationState.session_id;
    console.log(`Cleaning up session ${sessionId}...`);

    // Set navigating flag first to prevent UI flicker
    setIsNavigatingAway(true);

    // Stop all polling intervals
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (evaluationPollingIntervalRef.current) {
      clearInterval(evaluationPollingIntervalRef.current);
      evaluationPollingIntervalRef.current = null;
    }

    // Report voice mode usage if provided
    if (voiceModeUsed !== undefined && voiceModeSeconds !== undefined) {
      try {
        await handleApiCall(`/sessions/${sessionId}/voice-usage`, {
          method: 'POST',
          body: JSON.stringify({
            voice_mode_used: voiceModeUsed,
            voice_mode_seconds: voiceModeSeconds
          })
        });
        console.log(`Voice mode usage reported: used=${voiceModeUsed}, seconds=${voiceModeSeconds}`);
      } catch (error) {
        console.error('Failed to report voice mode usage:', error);
      }
    }

    // Call backend to stop simulation and cleanup first (don't await)
    handleApiCall(`/sessions/${sessionId}`, {
      method: 'DELETE'
    }).then(() => {
      console.log(`Session ${sessionId} cleaned up successfully`);
    }).catch((error) => {
      console.error(`Failed to cleanup session ${sessionId}:`, error);
    });

    // Clear all React state after navigation flag is set
    setSimulationState(null);
    setTaskList(null);
    setSemanticProfile(null);
    setObjectiveUpdates(null);
    setFinalEvaluation(null);
    setTimeTracking(null);
    setEventCodeMap({});
    setMessageInput('');
    setActivelyRespondingAgent(null);
    setError(null);
  }, [simulationState, handleApiCall]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // Auto-scroll when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [simulationState?.conversations]);

  // Cleanup polling intervals and session on unmount
  useEffect(() => {
    return () => {
      // Stop all polling
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      if (evaluationPollingIntervalRef.current) {
        clearInterval(evaluationPollingIntervalRef.current);
      }

      // Call backend to cleanup session if one exists
      if (simulationState?.session_id) {
        const sessionId = simulationState.session_id;
        handleApiCall(`/sessions/${sessionId}`, {
          method: 'DELETE'
        }).catch(error => {
          console.error(`Failed to cleanup session ${sessionId} on unmount:`, error);
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run cleanup on unmount, not when session_id changes

  const hookReturnValue = {
    // State
    simulationState,
    taskList,      // Dynamic task list with health tracking
    objectiveUpdates,  // Recent objective evaluation results
    semanticProfile,  // Semantic profile and simulation health
    finalEvaluation,  // Final evaluation report
    isLoading,
    error,
    messageInput,
    setMessageInput,
    isSendingMessage,
    isGeneratingEvaluation,
    messagesEndRef,
    timeTracking,  // Time tracking data
    showTimeExpiredModal,  // Flag to show time expired modal
    setShowTimeExpiredModal,  // Control modal visibility
    isNavigatingAway,  // Flag to indicate navigation is in progress (prevents UI flicker)
    eventCodeMap,  // Event code state management
    unreadAgents,  // Set of agent IDs with unread messages

    // Actions
    initializeSimulation,
    sendMessage,
    selectAgent,
    updateTask,
    clearError,
    generateFinalEvaluation,
    fetchEventCode,
    pushEventCode,
    cleanupSession,
    reportVoiceModeUsage,  // Report voice mode usage to backend
    setNewMessageCallback,  // Set callback for new message notifications
    clearAllUnread,  // Clear all unread indicators

    // Computed values
    selectedAgent: simulationState?.agents.find(a => a.agent_id === simulationState.selected_agent_id) || null,
    selectedConversation: simulationState?.selected_agent_id ?
      simulationState.conversations[simulationState.selected_agent_id] || [] : [],
    hasActiveSimulation: !!simulationState,

    // Utilities
    isAgentTyping: (agentId: string) => simulationState?.typing_agents.has(agentId) || false, // For sidebar
    isAgentTypingInChat: (agentId: string) => activelyRespondingAgent === agentId, // For in-chat typing bubble
    getAgentMessageCount: (agentId: string) => simulationState?.conversations[agentId]?.length || 0,
    hasUnreadMessages  // Check if an agent has unread messages
  };

  return hookReturnValue;
};

export default useV4Chat;
